import React, { useEffect, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { X } from 'lucide-react';

const APP_STORE = 'https://apps.apple.com/ca/app/carsjudge-ai-1-car-managment/id6749334935';
const PLAY_STORE = 'https://play.google.com/store/apps/details?id=com.carsjudge.ai';
const DISMISS_KEY = 'cj_app_banner_dismissed';

// Only promote the app download on public marketing pages — not inside the
// signed-in web app or auth flows.
const PUBLIC_PATHS = new Set(['/', '/pricing', '/about', '/support', '/media-kit', '/privacy', '/terms']);

function getPlatform(): 'ios' | 'android' | null {
  if (typeof navigator === 'undefined') return null;
  const ua = navigator.userAgent || '';
  if (/android/i.test(ua)) return 'android';
  if (/iphone|ipad|ipod/i.test(ua)) return 'ios';
  return null;
}

/**
 * Persistent, dismissible "Get the app" banner shown only on mobile widths
 * (md:hidden) and only on public marketing routes. Deep-links to the correct
 * store for the visitor's platform.
 */
const MobileAppBanner: React.FC = () => {
  const location = useLocation();
  const [dismissed, setDismissed] = useState(true); // hidden until we confirm not dismissed
  const [platform, setPlatform] = useState<'ios' | 'android' | null>(null);

  useEffect(() => {
    setPlatform(getPlatform());
    try {
      setDismissed(localStorage.getItem(DISMISS_KEY) === '1');
    } catch {
      setDismissed(false);
    }
  }, []);

  if (dismissed || !platform || !PUBLIC_PATHS.has(location.pathname)) return null;

  const href = platform === 'ios' ? APP_STORE : PLAY_STORE;

  const dismiss = () => {
    try {
      localStorage.setItem(DISMISS_KEY, '1');
    } catch {
      /* ignore */
    }
    setDismissed(true);
  };

  const trackClick = () => {
    try {
      (window as any).gtag?.('event', 'app_banner_click', { platform });
    } catch {
      /* ignore */
    }
  };

  return (
    <div className="fixed bottom-0 inset-x-0 z-50 md:hidden bg-white border-t border-gray-200 shadow-[0_-2px_12px_rgba(0,0,0,0.08)] px-3 py-2.5 flex items-center gap-3">
      <button onClick={dismiss} aria-label="Dismiss" className="text-gray-400 hover:text-gray-600 p-1">
        <X size={18} />
      </button>
      <img src="/01734CC0-A28E-4415-8CF9-08714C76263B.jpeg" alt="CarsJudge AI" className="h-10 w-10 rounded-xl flex-shrink-0" />
      <div className="flex-1 min-w-0">
        <p className="text-sm font-semibold text-gray-900 leading-tight">Get the CarsJudge AI app</p>
        <p className="text-xs text-gray-500 leading-tight">Free · Scan &amp; judge any car in seconds</p>
      </div>
      <a
        href={href}
        target="_blank"
        rel="noopener noreferrer"
        onClick={trackClick}
        className="flex-shrink-0 bg-brand text-white text-sm font-semibold px-5 py-2 rounded-full hover:bg-brand transition-colors"
      >
        Get
      </a>
    </div>
  );
};

export default MobileAppBanner;
